import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {
	ISections,
	IStaticContentStore,
	TArticle,
} from "data/stores/static_content/static_content.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {chain, includes, orderBy, toLower} from "lodash";
import {makeAutoObservable} from "mobx";

export interface IPrizesController extends ViewController {
	i18n: ILocalizationStore;

	get tabs(): ISections["sections"];

	findSectionsById(sectionId: number): TArticle[];
}

@injectable()
export class PrizesController implements IPrizesController {
	get tabs() {
		return chain(this._staticContentStore.sections)
			.filter((tab) => includes(toLower(tab.name), "prizes"))
			.filter((tab) => {
				return !this.isCountryUS
					? toLower(tab.name) !== "prizes (es-us)"
					: toLower(tab.name) !== "prizes";
			})
			.value();
	}

	get contents() {
		return this._staticContentStore.articles;
	}

	get isCountryUS() {
		return this._userStore.isCountryUS;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.StaticContentStore)
		private readonly _staticContentStore: IStaticContentStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	public findSectionsById(sectionId: number) {
		const sections = this.contents?.filter((article) => article.section_id === sectionId) ?? [];

		return orderBy(sections, ["position"], ["asc"]);
	}

	async init() {
		await this._staticContentStore.fetchStaticContent();
	}

	dispose() {
		return;
	}
}
